import { useCallback, useEffect, useRef, useState } from "react";
import type { Answers } from "../types";

const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — answers can contain internal org/KPI names.
const SAVE_DEBOUNCE_MS = 500;

interface StoredDraft {
  savedAt: number;
  answers: Answers;
  /** ISO timestamp of a successful transmission to DBI, null while unsent. */
  sentAt: string | null;
  /** Snapshot of the answers at send time, so later edits are detectable. */
  sentSnapshot: string | null;
}

function readDraft(storageKey: string): StoredDraft | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (Date.now() - parsed.savedAt > EXPIRY_MS) {
      window.localStorage.removeItem(storageKey);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * localStorage draft with 30-day expiry, restored post-mount only — the
 * server always renders empty/default state, so there is no hydration
 * mismatch to reconcile.
 *
 * `sentAt` survives reloads so a submitted briefing stays visibly submitted;
 * `sentSnapshot` lets the UI tell "sent and unchanged" from "sent, then edited".
 *
 * `storageKey` is per questionnaire — two briefings must never share a draft.
 */
export function useBriefingDraft(storageKey: string) {
  const [answers, setAnswers] = useState<Answers>({});
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [sentSnapshot, setSentSnapshot] = useState<string | null>(null);
  const [hasHydrated, setHasHydrated] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const draft = readDraft(storageKey);
    if (draft) {
      setAnswers(draft.answers);
      setSentAt(draft.sentAt ?? null);
      setSentSnapshot(draft.sentSnapshot ?? null);
    }
    setHasHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload: StoredDraft = { savedAt: Date.now(), answers, sentAt, sentSnapshot };
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(payload));
      } catch {
        // Storage full or unavailable (private browsing) — draft simply isn't persisted.
      }
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, sentAt, sentSnapshot, hasHydrated, storageKey]);

  const setAnswer = useCallback((questionId: string, value: Answers[string]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const mergeAnswers = useCallback((incoming: Answers) => {
    setAnswers((prev) => ({ ...prev, ...incoming }));
  }, []);

  const markSent = useCallback((snapshotAnswers: Answers) => {
    setSentAt(new Date().toISOString());
    setSentSnapshot(JSON.stringify(snapshotAnswers));
  }, []);

  const reset = useCallback(() => {
    setAnswers({});
    setSentAt(null);
    setSentSnapshot(null);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }, [storageKey]);

  const changedSinceSent = sentSnapshot !== null && JSON.stringify(answers) !== sentSnapshot;

  return {
    answers,
    setAnswer,
    mergeAnswers,
    reset,
    hasHydrated,
    sentAt,
    changedSinceSent,
    markSent,
  };
}
